import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ModalSimpleDatetimeComponent } from '../../parkifly-bookings/components/modal-simple-datetime/modal-simple-datetime.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router, RouterLink } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SelfServiceService } from '../../services/self-service.service';
import { CartService } from '../../services/cart.service';
import { ParkingNotFoundDialog } from '../drive-in-form/drive-in-form.component';
import { AuthService } from '../../services/auth.service';
import { LoginComponent } from '../login/login.component';
import { TopNavbarComponent } from '../../parkifly-bookings/organizations/components/top-navbar/top-navbar.component';
import { ManageBookingModalComponent } from '../../parkifly-bookings/components/manage-booking-modal/manage-booking-modal.component';

export const STATION_IDS = {
    arlanda: '5ac25e00-7863-4b8c-9fe1-eb8af7c7e77e',
    skavsta: '5bc25e00-7863-4b8c-9fe1-eb8af7c7e77e'
};

@Component({
    selector: 'app-customer-home',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        DatePipe,
        TranslocoModule,
        MatIconModule,
        MatMenuModule,
        MatSnackBarModule,
        MatProgressSpinnerModule,
        RouterLink,
        TopNavbarComponent
    ],
    templateUrl: './customer-home.component.html',
    styleUrls: ['./customer-home.component.css']
})
export class CustomerHomeComponent implements OnInit {
    currentYear = new Date().getFullYear();
    selectedAirport: 'arlanda' | 'skavsta' = 'arlanda';

    fromDate: Date | null = null;
    toDate: Date | null = null;

    driveInBookingForm = new FormGroup({
        registrationNumber: new FormControl('', [Validators.required])
    });

    isLoading = false;

    driveInInputs = {
        parkingFrom: '',
        parkingTo: '',
        gateName: '',
        parkingId: '',
        totalPrice: 0,
        orderId: '',
        orgId: '',
        vehicleOwner: null
    };

    constructor(
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private snackBar: MatSnackBar,
        private selfServiceService: SelfServiceService,
        private cartService: CartService,
        private router: Router,
        public authService: AuthService,
        private translocoService: TranslocoService
    ) { }

    ngOnInit(): void { }

    login(): void {
        this.dialog.open(LoginComponent, {
            width: '500px',
            disableClose: false,
            panelClass: 'login-dialog-panel',
            data: { forceMobileLayout: true, currentRoute: 'parkifly/my-orders/flygets' }
        });
    }

    goToHandleBooking(): void {
        this.dialog.open(ManageBookingModalComponent, {
            width: '90%',
            maxWidth: '440px',
            panelClass: 'manage-booking-dialog-panel'
        });
    }

    goToOrders(): void {
        this.router.navigate(['parkifly/my-orders/flygets']);
    }

    logout(): void {
        this.authService.logout();
    }

    get languageImageSource(): string {
        const lang = this.translocoService.getActiveLang();
        return lang === 'sv' ? 'assets/img/en.png' : 'assets/img/sv.png';
    }

    toggleLanguage(): void {
        const currentLang = this.translocoService.getActiveLang();
        const nextLang = currentLang === 'sv' ? 'en' : 'sv';
        this.translocoService.setActiveLang(nextLang);
    }

    selectAirport(airport: 'arlanda' | 'skavsta'): void {
        this.selectedAirport = airport;
    }

    openFromDatePicker(): void {
        const dialogRef = this.dialog.open(ModalSimpleDatetimeComponent, {
            width: '400px',
            data: {
                type: 'departure',
                selectedDate: this.fromDate,
                minDate: new Date()
            }
        });

        if (dialogRef.componentInstance) {
            dialogRef.componentInstance.selectDatetimeEvent.subscribe((res: any) => {
                const val = res?.datetime || res;
                if (val) {
                    this.fromDate = new Date(val);
                    if (this.toDate && this.toDate <= this.fromDate) {
                        const newTo = new Date(this.fromDate);
                        newTo.setDate(newTo.getDate() + 7);
                        this.toDate = newTo;
                    }
                    this.cdr.detectChanges();
                }
            });
        }
    }

    openToDatePicker(): void {
        const dialogRef = this.dialog.open(ModalSimpleDatetimeComponent, {
            width: '400px',
            data: {
                type: 'arrival',
                selectedDate: this.toDate,
                minDate: this.fromDate || new Date()
            }
        });

        if (dialogRef.componentInstance) {
            dialogRef.componentInstance.selectDatetimeEvent.subscribe((res: any) => {
                const val = res?.datetime || res;
                if (val) {
                    this.toDate = new Date(val);
                    this.cdr.detectChanges();
                }
            });
        }
    }

    regToUpper(): void {
        const control = this.driveInBookingForm.get('registrationNumber');
        if (control?.value) {
            control.setValue(control.value.toUpperCase());
        }
    }

    submitForm(): void {
        const licensePlate = this.driveInBookingForm.controls.registrationNumber.value;
        if (!licensePlate) {
            return;
        }

        this.isLoading = true;
        this.cdr.detectChanges();

        this.selfServiceService.driveUpAfterRegistration(licensePlate).then(result => {
            this.isLoading = false;
            this.cdr.detectChanges();
            if (result) {
                this.driveInInputs = {
                    parkingFrom: result.parkingFrom,
                    parkingTo: result.parkingTo,
                    gateName: result.gateName,
                    parkingId: result.parkingId,
                    totalPrice: result.totalPrice,
                    orderId: result.orderId,
                    orgId: result.organizationId,
                    vehicleOwner: result.vehicleOwner || null
                };
                this.goToDriveInBookingForm();
            } else {
                this.openParkingNotFoundDialog(licensePlate);
            }
        }).catch(() => {
            this.isLoading = false;
            this.cdr.detectChanges();
            this.openParkingNotFoundDialog(licensePlate);
        });
    }

    goToDriveInBookingForm(): void {
        const licensePlate = this.driveInBookingForm.get('registrationNumber')?.value;
        const queryParams: any = {
            l: licensePlate,
            parkingId: this.driveInInputs.parkingId,
            gateName: this.driveInInputs.gateName,
            parkingFrom: this.driveInInputs.parkingFrom,
            parkingTo: this.driveInInputs.parkingTo,
            price: this.driveInInputs.totalPrice,
            orderId: this.driveInInputs.orderId,
            orgId: this.driveInInputs.orgId
        };
        if (this.driveInInputs.vehicleOwner) {
            queryParams.vehicleOwner = JSON.stringify(this.driveInInputs.vehicleOwner);
        }
        this.router.navigate(['/parkifly/drive-in-details-form'], {
            queryParams,
            state: {
                licensePlate: licensePlate,
                parkingId: this.driveInInputs.parkingId,
                gateName: this.driveInInputs.gateName,
                parkingFrom: this.driveInInputs.parkingFrom,
                parkingTo: this.driveInInputs.parkingTo,
                price: this.driveInInputs.totalPrice,
                orderId: this.driveInInputs.orderId,
                orgId: this.driveInInputs.orgId,
                vehicleOwner: this.driveInInputs.vehicleOwner
            }
        });
    }

    openParkingNotFoundDialog(licensePlate: string): void {
        this.isLoading = false;
        this.cdr.detectChanges();

        const dialogRef = this.dialog.open(ParkingNotFoundDialog, {
            width: '400px',
            data: { licensePlate }
        });

        dialogRef.afterClosed().subscribe(() => {
            this.isLoading = false;
            this.cdr.detectChanges();
        });
    }

    private formatDateTimeISO(date: Date): string {
        const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
        const yyyy = date.getFullYear();
        const MM = pad(date.getMonth() + 1);
        const dd = pad(date.getDate());
        const hh = pad(date.getHours());
        const mm = pad(date.getMinutes());
        const ss = pad(date.getSeconds());
        return `${yyyy}-${MM}-${dd}T${hh}:${mm}:${ss}`;
    }

    onContinueSearch(): void {
        if (!this.fromDate || !this.toDate) {
            this.snackBar.open('Please select departure and arrival dates first.', 'OK', {
                duration: 3000,
                verticalPosition: 'top',
                horizontalPosition: 'center'
            });
            return;
        }

        const stationId = this.selectedAirport === 'skavsta' ? STATION_IDS.skavsta : STATION_IDS.arlanda;
        const fromStr = this.formatDateTimeISO(this.fromDate);
        const toStr = this.formatDateTimeISO(this.toDate);

        this.router.navigate(['/parkifly/search-results'], {
            queryParams: {
                stationId,
                airport: this.selectedAirport,
                from: fromStr,
                to: toStr
            }
        });
    }
}
